import { ApiQuestionType, AudiometryAnswerType } from "app/types";
import { QuizAnswerCognitiveType } from "interfaces";

const COLORS = {
  red: "#ff4d4d",
  orange: "#ffa500",
  yellow: "#ffff66",
  lightGreen: "#66ff66",
  green: "#4CAF50",
  blue: "#07d3ed",
};

type RespostaValidacao = {
  score: number;
  message: string;
  color: string;
};

type RespostaMap = {
  resposta: string;
  valor: string;
};

type BaseScore = {
  score: number;
  message?: string;
  color?: string;
  intrusions?: number;
};

export type ScoreResultType = BaseScore | null;

const normalizarPalavra = (palavra: string): string =>
  palavra
    .replace(/[.,;!?]$/, "")
    .trim()
    .toLowerCase();

function validarResposta(
  pergunta: string,
  resposta: string | null,
  data: Date | string,
): RespostaValidacao {
  if (!resposta) {
    return { score: 0, message: "Resposta ausente.", color: COLORS.red };
  }

  const dataFormatada = new Date(data);
  const ano = dataFormatada.getFullYear().toString();
  const mes = (dataFormatada.getMonth() + 1).toString().padStart(2, "0");
  const dia = dataFormatada
    .getDate()
    .toString()
    .padStart(2, "0");

  const respostasMap: Record<string, RespostaMap> = {
    "em que ano estamos?": {
      resposta: resposta.trim().padStart(4, "20"),
      valor: ano,
    },
    "em que mês estamos?": {
      resposta: resposta.trim().padStart(2, "0"),
      valor: mes,
    },
    "que dia do mês é hoje?": {
      resposta: resposta.trim().padStart(2, "0"),
      valor: dia,
    },
  };

  const validacao = respostasMap[pergunta];
  if (validacao && validacao.resposta === validacao.valor) {
    return { score: 1, message: "Resposta correta!", color: COLORS.green };
  }

  return { score: 0, message: "Resposta incorreta.", color: COLORS.red };
}

function verificarAcertos(
  palavrasUsuario: string | null,
  palavrasCorretas: string | string[],
): { score: number; intrusions: number; message: string; color: string } {
  if (!palavrasUsuario) {
    return {
      score: 0,
      intrusions: 0,
      message: "Resposta ausente.",
      color: COLORS.red,
    };
  }

  // Normaliza e separa as palavras do usuário
  const palavrasUsuarioArray = palavrasUsuario
    .split(",")
    .map(normalizarPalavra);

  // Normaliza e processa as palavras corretas (array ou string)
  const palavrasCorretasArrays = Array.isArray(palavrasCorretas)
    ? palavrasCorretas.map(correta => correta.split(",").map(normalizarPalavra))
    : [palavrasCorretas.split(",").map(normalizarPalavra)];

  let acertos: string[] = [];
  let intrusions = 0;
  let palavrasCorretasUsadas: string[] = [];

  // Verifica os acertos e intrusions na primeira lista correspondente
  for (const corretaArray of palavrasCorretasArrays) {
    if (
      palavrasUsuarioArray.some(usuarioPalavra =>
        corretaArray.includes(usuarioPalavra),
      )
    ) {
      palavrasCorretasUsadas = corretaArray;
      acertos = palavrasUsuarioArray.filter(p => corretaArray.includes(p));
      intrusions = palavrasUsuarioArray.filter(p => !corretaArray.includes(p))
        .length;
      break;
    }
  }

  // Calcula o percentual de acertos
  const acertosPercent = acertos.length / (palavrasCorretasUsadas.length || 1);

  // Determina a cor com base nos acertos
  const color =
    acertosPercent >= 0.75
      ? COLORS.green
      : acertosPercent >= 0.5
      ? COLORS.orange
      : acertos.length > 0
      ? COLORS.yellow
      : COLORS.red;

  // Mensagem final
  const message = `Acertou ${acertos.length} de ${
    palavrasCorretasUsadas.length
  }, com ${intrusions} intrusions. Acertos: ${acertos.join(", ")}.`;

  return { score: acertos.length, intrusions, message, color };
}

function contarPalavrasECalcularPontuacao(
  answer: QuizAnswerCognitiveType | AudiometryAnswerType,
): {
  score: number;
  message: string;
  color: string;
} {
  if (!answer.answer) {
    return { score: 0, message: "Resposta ausente.", color: COLORS.red };
  }

  // Extraindo o número da resposta (independentemente do prefixo)
  const match = answer.answer.match(/\d+/);
  const quantidadePalavras = match ? parseInt(match[0], 10) : 0;

  const ranges = [
    {
      min: 0,
      max: 5,
      score: 0,
      message: "Pontuação: Baixa",
      color: COLORS.red,
    },
    {
      min: 6,
      max: 8,
      score: 1,
      message: "Pontuação: Média",
      color: COLORS.orange,
    },
    {
      min: 9,
      max: 11,
      score: 2,
      message: "Pontuação: Alta",
      color: COLORS.yellow,
    },
    {
      min: 12,
      max: 14,
      score: 3,
      message: "Pontuação: Muito Alta",
      color: COLORS.lightGreen,
    },
    {
      min: 15,
      max: Infinity,
      score: 4,
      message: "Pontuação: Enorme",
      color: COLORS.green,
    },
  ];

  const range =
    ranges.find(
      r => quantidadePalavras >= r.min && quantidadePalavras <= r.max,
    ) || ranges[0];
  return { score: range.score, message: range.message, color: range.color };
}

function calcularScoreEscolaridade(
  escolaridade: string | null,
): {
  score: number;
  message: string;
} {
  if (!escolaridade) {
    return { score: 0, message: "Escolaridade ausente." };
  }

  const scores: Record<string, { score: number; message: string }> = {
    "sem escolaridade formal": { score: 2, message: "Sem escolaridade formal" },
    "1 - 3 anos de escolaridade": {
      score: 1,
      message: "1 - 3 anos de escolaridade",
    },
    "4+ anos de escolaridade": { score: 0, message: "4+ anos de escolaridade" },
  };

  return scores[escolaridade] || { score: 0, message: "Texto inválido" };
}

export const getScore = (
  question: ApiQuestionType,
  answer: QuizAnswerCognitiveType | AudiometryAnswerType,
  categoryCognitiveQuestionId: number,
): ScoreResultType => {
  const rightAnswer = question.rightAnswer
    ? question.rightAnswer.toString()
    : "sapato, casa, pente, chave, avião, balde, tartaruga, livro, colher, árvore";
  const handlers: Record<number, () => ScoreResultType> = {
    1: () => validarResposta(question.quest, answer.answer, answer.initDate!),
    2: () =>
      verificarAcertos(answer.answer, [
        "Óculos, Caneta, Martelo",
        "Chapéu, Moeda, Lanterna",
        "Relógio, Chave, Varssoura",
      ]),
    3: () => contarPalavrasECalcularPontuacao(answer),
    5: () => calcularScoreEscolaridade(answer.answer),
    6: () => verificarAcertos(answer.answer, rightAnswer),
    7: () => verificarAcertos(answer.answer, rightAnswer),
    8: () => verificarAcertos(answer.answer, rightAnswer),
    9: () => contarPalavrasECalcularPontuacao(answer),
    10: () => {
      const score =
        typeof answer.answer === "number"
          ? answer.answer
          : Number(answer.answer);
      return score >= 0
        ? { score, color: COLORS.green }
        : { score: 0, color: COLORS.red };
    },
    11: () => verificarAcertos(answer.answer, rightAnswer),
    12: () => verificarAcertos(answer.answer, rightAnswer),
    13: () =>
      answer.answer === "Não, nenhuma"
        ? { score: 0, color: COLORS.red }
        : { score: 1, color: COLORS.green },
  };

  return handlers[categoryCognitiveQuestionId]?.() ?? null;
};
